import React, { useState, useEffect, MouseEventHandler } from "react";
import CodeBox from "./CodeBox";
import SnippetCode from "./SnippetCode";
import AnswerCard from "./AnswerCard";
import Explanation from "./Explanation";
import Result from "./Result";
import { ITasks } from "./ITasks";
import { replace, AnswerStatus, arraysAreEqual } from "./helpers";
import { DragDropContext } from "@hello-pangea/dnd";
import SnippetSelection from "./SnippetSelection";

type Props = {
  stopQuiz: MouseEventHandler;
  questionsCount: number;
};

// TODO Um das gesamte Fenster einen hellgrauen Rand (ggf. mit leichtem Schatten) legen. Nur im Website Modus.
// TODO Zurück Button auf Intro Seite

const Quiz = ({ stopQuiz, questionsCount }: Props) => {
  const [quizFinished, setQuizFinished] = useState(false);
  const [tasks, setTasks] = useState<ITasks>();
  const [currQuestionIdx, setCurrentQuestionIdx] = useState(0);
  const [selected, setSelected] = useState([] as string[][]);
  const [answerStatus, setAnswerStatus] = useState([] as AnswerStatus[]);

  const fetchData = async (questionsCount: number) => {
    try {
      // let url = "https://quiz-backend-ochre.vercel.app/entries?count=" + questionsCount;;

      let url =
        process.env["REACT_APP_API_URL"] || "http://localhost:3001/entries";

      // let url = "http://localhost:3001/testentries";

      url += "?count=" + questionsCount;

      const response = await fetch(url);
      const data: ITasks = await response.json();
      setTasks(data);
      setSelected(Array(data.length).fill([]));
      setAnswerStatus(Array(data.length).fill(AnswerStatus.notAnswered));
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchData(questionsCount);
  }, [questionsCount]);

  function handleOnDragEnd(result: any) {
    if (!result.destination) return;

    setSelected((prev) => {
      const task = tasks![currQuestionIdx];
      const content = task?.answer.answers[result.draggableId];

      let prevCopy = [...prev];

      let arr = [...prevCopy[currQuestionIdx]!];
      if (prevCopy[currQuestionIdx]?.length === 0) {
        arr = task!.answer.correctSnippets!.map(() => "");
      }

      arr![parseInt(result.destination.droppableId)] = content!;
      prevCopy[currQuestionIdx] = arr;
      return prevCopy;
    });
  }

  const Task = () => {
    if (tasks === null || tasks === undefined) {
      return (
        <div className="d-flex justify-content-center mt-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      );
    }

    const task = tasks[currQuestionIdx];

    const selectedAnswers = selected[currQuestionIdx];

    if (!task) {
      throw new Error("task is undefined");
    }
    if (!selectedAnswers) {
      throw new Error("selectedAnswers is undefined");
    }

    const questionCode = task.question.source
      ? task.question.source.join("\n")
      : null;

    const enableCheckButton =
      (task.type === "snippets" &&
        selectedAnswers.length === task.answer.correctSnippets!.length &&
        selectedAnswers.every((e) => e !== "")) ||
      (task.type === "multipleChoice" &&
        ((task.answer.correctAnswers!.length === 1 &&
          selectedAnswers.length > 0) ||
          (task.answer.correctAnswers!.length > 1 &&
            selectedAnswers.length > 1)));

    function userAnswerIsCorrect() {
      if (task?.type === "multipleChoice") {
        return arraysAreEqual(
          task.answer.correctAnswers!.map((i) => task.answer.answers[i]!),
          selectedAnswers!
        );
      }

      if (task?.type === "snippets") {
        return (
          JSON.stringify(task.answer.correctSnippets!) ===
          JSON.stringify(selectedAnswers!)
        );
      }

      throw new Error("Unknown task type");
    }

    const isLastQuestion = currQuestionIdx === tasks.length - 1;
    const revealed = answerStatus[currQuestionIdx] !== AnswerStatus.notAnswered;

    const leftColStyle =
      "d-none d-md-block col-md-3 col-lg-3 col-xl-2 fw-medium";

    return (
      <DragDropContext onDragEnd={handleOnDragEnd}>
        <div className="container py-3">
          <div className="row align-items-center">
            {/* Header with left and right buttons */}
            <div className="col">
              <button
                type="button"
                className="btn btn-outline-secondary rounded-circle"
                disabled={currQuestionIdx === 0}
                onClick={() => {
                  setCurrentQuestionIdx((idx) => idx - 1);
                }}
              >
                <span className="bi bi-chevron-left"></span>
              </button>
            </div>

            <div className="col-auto px-0 text-center">
              Question {currQuestionIdx + 1} of {tasks.length}
            </div>

            <div className="col text-end">
              <button
                type="button"
                className="btn btn-outline-secondary rounded-circle"
                disabled={isLastQuestion}
                onClick={() => {
                  setCurrentQuestionIdx((idx) => idx + 1);
                }}
              >
                <span className="bi bi-chevron-right"></span>
              </button>
            </div>
          </div>
          <div className="row pt-3">
            <hr />
          </div>

          {/* Question row */}
          <div className="row">
            <div className={leftColStyle}>
              <i className="bi bi-patch-question m-0 p-0" />
              &nbsp;&nbsp;Question
            </div>
            <div className="col">
              <div className="row mb-4">
                <div className="d-inline-flex gap-2">
                  <span className="badge bg-secondary">{task.category}</span>
                  {revealed && (
                    <ResultBadge isCorrect={userAnswerIsCorrect()} />
                  )}
                </div>
              </div>

              <div className="row">
                <div
                  className="fw-medium fs-5"
                  dangerouslySetInnerHTML={{
                    __html: replace(task.question.text),
                  }}
                />
                <div className="my-3">
                  {task.type === "snippets" && (
                    <SnippetCode
                      code={questionCode!}
                      revealed={revealed}
                      selectedSnippets={selectedAnswers}
                      correctSnippets={task.answer.correctSnippets!}
                      onSelectedSnippets={(snippets) => {
                        let selectedCopy = [...selected];
                        selectedCopy[currQuestionIdx] = snippets;
                        setSelected(selectedCopy);
                      }}
                    />
                  )}

                  {task.type === "multipleChoice" && questionCode && (
                    <CodeBox code={questionCode} />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Answers/Snippets row */}
          <div className="row">
            <div className={leftColStyle}>
              <i className="bi bi-list" />
              &nbsp;&nbsp;{task.type === "snippets" ? "Snippets" : "Answers"}
            </div>

            <div className="col">
              <div className="mb-3">
                <div className="d-flex flex-column gap-3">
                  {task.type === "snippets" && (
                    <SnippetSelection providedSnippets={task.answer.answers} />
                  )}

                  {task.type === "multipleChoice" &&
                    task.answer.answers.map((answer, index) => (
                      <AnswerCard
                        key={index}
                        title={
                          <div
                            dangerouslySetInnerHTML={{
                              __html: replace(answer),
                            }}
                          />
                        }
                        selectable={!revealed}
                        selected={selectedAnswers.includes(answer)}
                        correct={
                          revealed &&
                          task.answer.correctAnswers!.includes(index)
                        }
                        incorrect={
                          revealed &&
                          !task.answer.correctAnswers!.includes(index)
                        }
                        onlyOneAnswerIsCorrect={
                          task.answer.correctAnswers!.length === 1
                        }
                        onClick={() => {
                          setSelected((prev) => {
                            let prevCopy = [...prev];
                            if (prevCopy[currQuestionIdx]!.includes(answer)) {
                              const arr = prevCopy[currQuestionIdx]!.filter(
                                (e) => e !== answer
                              ); // remove element
                              prevCopy[currQuestionIdx] = arr;
                              return prevCopy;
                            }

                            if (task.answer.correctAnswers!.length === 1) {
                              prevCopy[currQuestionIdx] = [answer];
                              return prevCopy;
                            }
                            prevCopy[currQuestionIdx] = [
                              ...prevCopy[currQuestionIdx]!,
                              answer,
                            ];
                            return prevCopy;
                          });
                        }}
                      />
                    ))}
                </div>
              </div>
            </div>
          </div>

          {/* Explanation row */}
          {revealed && task.explanation != null && (
            <div className="row">
              <div className={leftColStyle}>
                <i className="bi bi-info-circle" />
                &nbsp;&nbsp;Explanation
              </div>
              {/* Show 'Explanation' on XS size */}
              <div className="d-block d-md-none mt-4 mb-1">
                <h4>Explanation</h4>
              </div>
              <div className="col">
                <Explanation
                  topText={task.explanation.topText}
                  bottomText={task.explanation.bottomText}
                  source={task.explanation.source}
                />
              </div>
            </div>
          )}
          {/* Buttons */}
          <div className="row">
            <div className={leftColStyle}></div>

            {/* Buttons */}
            <div className="col my-4">
              <div className="d-inline-flex gap-4">
                <div className="col-6 col-md-auto">
                  <button
                    type="button"
                    className="btn btn-lg btn-primary"
                    disabled={!enableCheckButton}
                    onClick={() => {
                      setAnswerStatus((prev) => {
                        if (task.type === "multipleChoice") {
                          return prev.map((item, index) =>
                            index === currQuestionIdx
                              ? userAnswerIsCorrect()
                                ? AnswerStatus.correct
                                : AnswerStatus.incorrect
                              : item
                          );
                        }
                        if (task.type === "snippets") {
                          return prev.map((item, index) =>
                            index === currQuestionIdx
                              ? arraysAreEqual(
                                  task.answer.correctSnippets!,
                                  selectedAnswers
                                )
                                ? AnswerStatus.correct
                                : AnswerStatus.incorrect
                              : item
                          );
                        }

                        throw new Error("Unknown task type");
                      });
                    }}
                  >
                    <span className="bi-check-lg" />
                    &nbsp;&nbsp; Check
                  </button>
                </div>

                <div className="col-6 col-md-auto ">
                  <button
                    type="button"
                    className="btn btn-lg btn-primary"
                    disabled={!revealed}
                    onClick={() => {
                      const isLastQuestion =
                        currQuestionIdx === tasks.length - 1;
                      if (isLastQuestion) {
                        setQuizFinished(true);
                        return;
                      }

                      setCurrentQuestionIdx((idx) => idx + 1);
                    }}
                  >
                    Next&nbsp;&nbsp;
                    <span className="bi-chevron-right" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DragDropContext>
    );
  };

  if (!quizFinished) {
    return <Task />;
  }

  if (!tasks) {
    throw new Error("tasks is undefined");
  }

  return (
    <Result
      answerStatus={answerStatus}
      retry={() => {
        setQuizFinished(false);
        setAnswerStatus(Array(tasks.length).fill(AnswerStatus.notAnswered));
        setSelected(Array(tasks.length).fill([]));
        setCurrentQuestionIdx(0);
      }}
      stopQuiz={stopQuiz}
    />
  );
};

type ResultBadgeProps = {
  isCorrect: boolean;
};

const ResultBadge = (props: ResultBadgeProps) => {
  return (
    <span className={`badge ${props.isCorrect ? "bg-success" : "bg-danger"}`}>
      <span
        className={`bi ${
          props.isCorrect ? "bi-check-circle-fill" : "bi-x-circle-fill"
        } me-2`}
      />
      {props.isCorrect ? "Correct" : "Incorrect"}
    </span>
  );
};

export default Quiz;
